import firebase from '@modules/firebase';

import { toUser, toUserTags, toDBUser, sanitizeForDb, toStripeSubscription } from '@modules/transformers';
import { assertIsType, assertIsTypedArray, has } from '@modules/types';
import { isDBUser, isDBUserTags } from './predicates/user-predicates';

import type { AnonymousUser, StripeSubscription, User, UserTags } from 'types';
import { DBStripeSubscription } from 'types/db';
import { analytics } from '@modules/analytics';
import { logger } from '@modules/logger';

/**
 * Gets the users details
 * @param userId The ID of the user
 */
export async function getUser(userId: string): Promise<User | undefined> {
  const usersCollectionRef = firebase.firestore().collection('users');
  const userDocRef = usersCollectionRef.doc(userId);

  const userSnapshot = await userDocRef.get();

  // We could be operating as an anonymous user so there will be no corresponding entry in the DB
  if (!userSnapshot.exists) return;

  const user = userSnapshot.data();
  assertIsType(user, isDBUser);

  return toUser(user);
}

interface UserDetails {
  user: User | undefined;
  userTags: UserTags | undefined;
  userSubscriptions: StripeSubscription[];
}

export async function getUserDetails(userId: string): Promise<UserDetails> {
  const [user, userTags, userSubscriptions] = await Promise.all([
    getUser(userId),
    getUserTags(userId),
    getUserSubscriptions(userId),
  ]);

  return { user, userTags, userSubscriptions };
}

export async function getUserSubscriptions(userId: string): Promise<StripeSubscription[]> {
  const usersCollectionRef = firebase.firestore().collection('users');
  const userDocRef = usersCollectionRef.doc(userId);
  const subscriptionsCollectionRef = userDocRef.collection('subscriptions');

  const snapshot = await subscriptionsCollectionRef.get();
  const dbSubscriptions = snapshot.docs.map((doc) => doc.data()) as DBStripeSubscription[];
  const subscriptions = dbSubscriptions.map((subscription) => toStripeSubscription(subscription));

  return subscriptions;
}

export function subscribeToUserSubscriptions(
  userId: string,
  onUpdate: (subscriptions: StripeSubscription[]) => void
): () => void {
  const usersCollectionRef = firebase.firestore().collection('users');
  const userDocRef = usersCollectionRef.doc(userId);
  const subscriptionsCollectionRef = userDocRef.collection('subscriptions');

  const unsubscribe = subscriptionsCollectionRef.onSnapshot((subscriptionsSnapshot) => {
    const dbSubscriptions = subscriptionsSnapshot.docs.map((doc) => doc.data()) as DBStripeSubscription[];
    const subscriptions = dbSubscriptions.map((subscription) => toStripeSubscription(subscription));

    onUpdate(subscriptions);
  });

  return unsubscribe;
}

export function subscribeToUser(userId: string, onUpdate: (user: User | undefined) => void): () => void {
  const usersCollectionRef = firebase.firestore().collection('users');
  const userDocRef = usersCollectionRef.doc(userId);

  const unsubscribe = userDocRef.onSnapshot((userSnapshot) => {
    if (!userSnapshot.exists) {
      onUpdate(undefined);
      return;
    }

    const dbMobileUser = userSnapshot.data();

    assertIsType(dbMobileUser, isDBUser);

    onUpdate(toUser(dbMobileUser));
  });

  return unsubscribe;
}

export async function getAllWorkspaceUsers(orgId: string): Promise<User[]> {
  const collectionRef = firebase.firestore().collection('users');
  const docsQuery = collectionRef.where('org_ids', 'array-contains', orgId);

  const snapshot = await docsQuery.get();
  const users = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(users, isDBUser);

  return users.map((user) => toUser(user));
}

export async function getUserTags(userId: string): Promise<UserTags | undefined> {
  const collectionRef = firebase.firestore().collection('user_tags');
  const docRef = collectionRef.doc(userId);

  const snapshot = await docRef.get();

  if (snapshot.exists) {
    const dbUserTags = snapshot.data();
    assertIsType(dbUserTags, isDBUserTags);

    return toUserTags(dbUserTags);
  }
}

export function subscribeToUserTags(
  userId: string,
  onUpdate: (userTags: UserTags | undefined) => void
): () => void {
  const collectionRef = firebase.firestore().collection('user_tags');
  const docRef = collectionRef.doc(userId);

  const unsubscribe = docRef.onSnapshot((snapshot) => {
    if (!snapshot.exists) {
      onUpdate(undefined);
      return;
    }

    const dbUserTags = snapshot.data();

    assertIsType(dbUserTags, isDBUserTags);

    onUpdate(toUserTags(dbUserTags));
  });

  return unsubscribe;
}
